import { children, createEffect, createSignal, onMount, ParentProps, Show } from "solid-js"
import Leaderboard from "./components/Leaderboard"
import Rewards from "./components/Rewards"
import Gossip from "./components/Gossip"
import { useSwarm } from "./SwarmContext"
import LoadingSpinner from "./components/LoadingSpinner"
import { swarmVariant } from "./lib/swarmVariant"
import { appEnv } from "./lib/appEnv"
import { GlobalStats } from "./components/GlobalStats"
import { AuthModal, LogOutButton } from "./components/AuthModal"
import { Button } from "./ui/Button"
import SectionHeader from "./components/SectionHeader"
import PeerTable from "./components/PeerTable"
import swarmApi from "./swarm.apiv1"
import { assertNever } from "./lib/assertNever"
import { FarcasterIcon, GensynIcon, TwitterIcon } from "./components/Icon"
import Hr from "./components/Hr"

export default function Swarm() {
	const ctx = useSwarm()

	const premadeTweet = encodeURIComponent("Running RL Swarm on @gensynai testnet 🔥\n\nhttps://testnet.gensyn.ai")
	const titleSuffix = (() => {
		switch (swarmVariant) {
			case "rl-swarm-m1":
				return "RL Swarm Math"
			case "rl-swarm-m2":
				return "RL Swarm Math-hard"
			case "genrl-swarm":
				return "GenRL Swarm"
			default:
				assertNever(swarmVariant)
		}
	})()

	const [isOpen, setIsOpen] = createSignal(false)

	onMount(() => {
		console.log(`${appEnv} running ${swarmVariant}`)
	})

	createEffect(async () => {
		try {
			const whoami = await swarmApi.whoami()
			if (whoami.eoa) {
				ctx.setUserEoa(whoami.eoa as `0x${string}`)
			}
		} catch (error) {
			console.error(error)
		}
	})

	return (
		<>
			<Show when={swarmVariant !== "genrl-swarm"}>
				<div class="bg-secondary w-full p-2 pb-4 !antialiased">
					<fieldset role="group" class="bg-transparent border-secondary-content border-2 text-center p-2 pb-4">
						<legend class="ml-4 ml-2 text-secondary-content bg-secondary font-bold uppercase">Attention:</legend>
						<p class="uppercase text-secondary-content p-2 font-normal mb-4 block md:inline">This dashboard is now read-only. Historical data is preserved on-chain.&nbsp;</p>
						<div class="block md:inline">
							<a href="https://dashboard.gensyn.ai" class="bg-secondary-content text-secondary uppercase cursor-pointer p-2">
								Go to latest dashboard &rarr;
							</a>
						</div>
					</fieldset>
				</div>
			</Show>

			<main class="tracking-wider !font-normal !antialiased max-w-[876px] p-2 md:pt-12 md:pl-12 md:pr-0 flex flex-col justify-between md:min-h-screen ml-auto mr-auto">
				{/* Math, Math-Hard header. TODO: Kill off this block when we're done with math. */}
				<Show when={swarmVariant !== "genrl-swarm"}>
					<header class="mb-8">
						<div class="flex flex-row justify-between">
							<div class="flex flex-col items-start mb-4">
								<h1 class="uppercase text-2xl tracking-[0.25em]">Gensyn Testnet:</h1>
								<h2 class="uppercase text-2xl tracking-[0.25em]">{titleSuffix}</h2>
							</div>
							<div class="flex flex-row gap-4 grow-0 shrink-1 items-start">
								<Show when={!ctx.userEoa()} fallback={<LogOutButton setUserEoa={ctx.setUserEoa} />}>
									<Button
										onClick={() => {
											setIsOpen(true)
										}}
										content="login"
									/>
								</Show>
								<a href="https://github.com/gensyn-ai/rl-swarm" class="filter brightness-95 uppercase border border-primary-content bg-primary text-primary-content p-2" target="_blank" rel="noopener noreferrer">
									Join the swarm
								</a>
							</div>
						</div>
						<Hr glyph="dot" fill="primary-content" />
						<Show when={!ctx.userEoa()}>
							<p class="text-lg uppercase mt-4 mb-4">Join by running an RL Swarm node to train your local model using swarm intelligence. Track your contributions below.</p>
							<p class="uppercase mt-8 mb-8 flex flex-row gap-8 items-center">
								<a href="https://www.gensyn.ai/articles/rl-swarm" target="_blank" rel="noopener noreferrer" class="underline">
									Learn about RL Swarm
								</a>
							</p>
							<Hr glyph="dot" fill="base-content" />
						</Show>
					</header>
				</Show>
				{/* End Math, Math-Hard header */}

				{/* GenRL Swarm header */}
				<Show when={swarmVariant === "genrl-swarm"}>
					<header class="mb-8">
						<div class="flex flex-col md:flex-row justify-between">
							<div class="flex flex-col items-center md:items-start mb-4 grow-0 shrink-1">
								<img class="w-80" src="/images/gen-RLSwarm-UI-logo-.png" alt="RL Swarm logo" />
							</div>
							<div class="flex grow-1 flex-col-reverse md:flex-col gap-4 items-start">
								<div class="flex flex-row gap-4 justify-center md:justify-end w-full">
									<Show when={!ctx.userEoa()} fallback={<LogOutButton setUserEoa={ctx.setUserEoa} width="wide" />}>
										<Button
											width="wide"
											onClick={() => {
												setIsOpen(true)
											}}
											content="login"
										/>
									</Show>
									<Button width="wide" colorway="primary" content="Join the swarm" onClick={() => window.open("https://github.com/gensyn-ai/rl-swarm", "_blank")} />
								</div>
								<div class="font-mono uppercase text-center md:text-right text-lg flex grow-1 shrink-0 flex-col w-full">
									<p>Gensyn Testnet: RL Swarm</p>
									<p>Reasoning Gym</p>
								</div>
							</div>
						</div>
						<Show when={!ctx.userEoa()}>
							<p class="text-lg uppercase mt-4 mb-4 text-center md:text-left">Join by running an RL Swarm node to train your local model using swarm intelligence and track your contributions.</p>
							<p class="uppercase mt-8 mb-16 flex flex-row gap-8 items-center justify-center md:justify-start">
								<span>Now Live: GenRL</span>
								<a href="https://www.gensyn.ai/articles/rl-swarm" target="_blank" rel="noopener noreferrer" class="underline">
									Learn about RL Swarm
								</a>
							</p>
						</Show>
						<Hr glyph="asterisk" fill="base-content" />
					</header>
				</Show>
				{/* End GenRL Swarm header */}

				<article class="flex flex-col align-top flex-1">
					<Show when={ctx.userEoa()}>
						<div class="flex flex-row justify-between mb-2">
							<SectionHeader title="Your Nodes" />
							<p>{ctx.userEoa()}</p>
						</div>
						<div class="mb-10">
							<Show when={swarmVariant === "genrl-swarm"}>
								<PeerTable data={ctx.currentUserPeers()} theme="normal" loading={ctx.currentUserPeersLoading()} error={ctx.currentUserPeersError()} />
							</Show>
							<Show when={swarmVariant !== "genrl-swarm"}>
								<PeerTable data={ctx.currentUserPeers()} theme="inverted" loading={ctx.currentUserPeersLoading()} error={ctx.currentUserPeersError()} />
							</Show>
						</div>
					</Show>
					<GlobalStats />
					<Leaderboard />
					<div class="mt-8 mb-8">
						<Hr glyph="dot" fill="base-content" />
					</div>

					<div class="flex-none mb-2 border border-2 border-dotted p-2 bg-base-200 border-base-content">
						<Show
							when={ctx.roundAndStageLoading() || ctx.roundAndStageError()}
							fallback={
								<Show when={swarmVariant !== "genrl-swarm"} fallback={<>Round: {ctx.roundAndStage()?.round}</>}>
									Round: {ctx.roundAndStage()?.round} Stage: {ctx.roundAndStage()?.stage}
								</Show>
							}
						>
							<LoadingSpinner message="Loading round and stage..." />
						</Show>
					</div>

					<div class="flex flex-col md:flex-row gap-4 mb-8 mt-4">
						<div class="w-full md:w-1/2">
							<Rewards />
						</div>
						<div class="w-full md:w-1/2">
							<Gossip />
						</div>
					</div>

					<Banner>
						<div class="flex flex-row justify-between">
							<div class="my-8 uppercase flex items-center">
								<ul class="list-none flex gap-8 flex-col md:flex-row">
									<li>
										<a class="underline" href="https://gensyn-testnet.explorer.alchemy.com/" target="_blank" rel="noopener noreferrer">
											Block Explorer
										</a>
									</li>
									<li>
										<a class="underline" href="https://huggingface.co/Gensyn" target="_blank" rel="noopener noreferrer">
											Hugging Face
										</a>
									</li>
									<li>
										<a class="underline" href="https://gensyn.ai/testnet" target="_blank" rel="noopener noreferrer">
											Testnet
										</a>
									</li>
								</ul>
							</div>
							<div class="flex items-center flex-col md:flex-row">
								<a class="flex items-center" href="https://gensyn.ai" target="_blank" rel="noopener noreferrer">
									<img class="h-[50px]" src="/images/logo.gif" alt="A spinning gensyn logo" />
									<div class="h-[30px] text-base-content">
										<GensynIcon />
									</div>
								</a>
								<div class="ml-8 uppercase">gensyn &copy;2025</div>
							</div>
						</div>
					</Banner>

					<footer class="flex items-center mt-8 text-base-content">
						<a target="_blank" rel="noopener noreferrer" href="https://x.com/gensynai">
							<span class="inline-block max-h-[30px] min-h-[30px] text-base-content">
								<TwitterIcon />
							</span>
						</a>
					</footer>
				</article>
				<AuthModal isOpen={isOpen} setIsOpen={setIsOpen} userEoa={ctx.userEoa} setUserEoa={ctx.setUserEoa} dataset={titleSuffix} />
			</main>
		</>
	)
}

function Banner(props: ParentProps) {
	const resolvedChildren = children(() => props.children)
	return (
		<>
			<Hr glyph="dot" fill="base-content" />
			{resolvedChildren()}
			<Hr glyph="slash" fill="base-content" />
		</>
	)
}
